import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '@/lib/apiAuth';
import { sendEmail, sendWelcomeEmail, sendAlertEmail, sendPasswordResetEmail, sendSummaryEmail } from '@/lib/email';

/**
 * Test Email API Endpoint
 * POST /api/test-email
 * 
 * Body: {
 *   type: 'basic' | 'welcome' | 'alert' | 'reset' | 'summary',
 *   to: string (optional, defaults to test email)
 * }
 */

export default withAuth(async (req: NextApiRequest, res: NextApiResponse, user) => {
  if (req.method !== 'POST') {
    return res.status(405).json({ error: 'Método no permitido' });
  }

  // Only allow admins or specific test scenarios
  if ((user as any).rol !== 'admin' && process.env.NODE_ENV !== 'development') {
    return res.status(403).json({ error: 'Solo administradores pueden enviar emails de prueba' });
  }

  try {
    const { type = 'basic', to = 'gabrielcustodiooliveira@gmail.com' } = req.body;

    let result;

    switch (type) {
      case 'welcome':
        result = await sendWelcomeEmail(to, {
          name: (user as any).nombre || 'Usuario de Prueba',
          role: (user as any).rol as 'admin' | 'asesor' | 'cliente',
        });
        break;

      case 'alert':
        result = await sendAlertEmail(to, {
          severity: 'high',
          message: 'Esta es una alerta de prueba para verificar el sistema de notificaciones por email.',
          rule_name: 'Regla de Prueba - Sistema de Alertas',
          created_at: new Date().toISOString(),
        });
        break;

      case 'reset':
        result = await sendPasswordResetEmail(to, 'https://externaliza2.gabsdev.com/auth/reset?token=test-token-123');
        break;

      case 'summary':
        result = await sendSummaryEmail(to, {
          period: 'daily',
          metrics: {
            newDocuments: 12,
            processedByAI: 8,
            alerts: 2,
            syncStatus: 'success',
          },
        });
        break;

      case 'basic':
      default:
        result = await sendEmail({
          to,
          subject: 'Test de Email - Externaliza2',
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
              <h1 style="color: #1babe4;">✅ Test de Email Exitoso</h1>
              <p>Este es un email de prueba para verificar que la configuración de Resend está funcionando correctamente.</p>
              <p><strong>Fecha:</strong> ${new Date().toLocaleString('es-ES')}</p>
              <p><strong>Usuario:</strong> ${user.email}</p>
              <hr style="margin: 20px 0; border: none; border-top: 1px solid #e2e8f0;" />
              <p style="color: #64748b; font-size: 12px;">
                Externaliza2 - Sistema de Gestión Contable
              </p>
            </div>
          `,
        });
        break;
    }

    if (!result.success) {
      return res.status(500).json({
        success: false,
        error: result.error,
        message: 'Error al enviar el email',
      });
    }

    return res.status(200).json({
      success: true,
      message: `Email de tipo "${type}" enviado exitosamente a ${to}`,
      emailId: result.id,
      recipient: to,
      type,
    });

  } catch (error: any) {
    console.error('[API test-email] Error:', error);
    return res.status(500).json({
      success: false,
      error: error.message || 'Error interno del servidor',
    });
  }
});
